import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { animateNext } from 'lib/animateNext';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { eventAseUpdateRiskRating, NAVIGATE_MODAL_BACK } from 'store/actions';
import {
  selectAseComplianceType,
  selectAseAssetRiskRatings,
} from 'store/assessments/selectors';
import { Details } from 'store/assessments/types';

type DraftChanges = {
  riskLikelihoodId: string;
  riskSeverityId: string;
  riskChangeReason: boolean;
  riskReason: string;
};

export const RiskRatingScreen: React.FC<
  ModalScreenProps<ModalScreens.ASERISKRATING>
> = ({ route, navigation }) => {
  const { eventKey, data } = route.params;
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();
  const complianceType = useSelector(selectAseComplianceType);

  const [currentlyEditing, setCurrentlyEditing] = React.useState<
    undefined | 'likelihood' | 'severity'
  >();

  const [assetLikelihoodId, assetSeverityId, assetReason = ''] = useSelector(
    selectAseAssetRiskRatings,
  )[complianceType.id] || ['0', '0', ''];

  const dataRef = React.useRef<DraftChanges>({ ...data });
  const initDataRef = React.useRef<DraftChanges>(cloneDeep(dataRef.current));

  const isDirtyRef = React.useRef(false);
  const checkIsDirty = React.useCallback(
    () => !isEqual(initDataRef.current, dataRef.current),
    [],
  );
  isDirtyRef.current = checkIsDirty();

  const matrix = complianceType.matrix!;

  const currentRisk:
    | {
        likelihoodId: string;
        likelihood: string;
        likelihoodDescription: string;
        severityId: string;
        severity: string;
        severityDescription: string;
        rating: string;
        ratingDescription: string;
      }
    | undefined = matrix.outcomes[`${assetLikelihoodId}x${assetSeverityId}`]
    ? {
        likelihoodId: assetLikelihoodId,
        likelihood: matrix.likelihoods[assetLikelihoodId]!.name,
        likelihoodDescription:
          matrix.likelihoods[assetLikelihoodId]!.description ||
          'No Description',
        severityId: assetSeverityId,
        severity: matrix.severities[assetSeverityId]!.name,
        severityDescription:
          matrix.severities[assetSeverityId]!.description || 'No Description',
        rating:
          matrix.ratings[
            matrix.outcomes[`${assetLikelihoodId}x${assetSeverityId}`]!
          ]!.name,
        ratingDescription:
          matrix.ratings[
            matrix.outcomes[`${assetLikelihoodId}x${assetSeverityId}`]!
          ]!.description || 'No Description',
      }
    : undefined;

  const newRisk:
    | {
        rating: string;
        ratingDescription: string;
      }
    | undefined = matrix.outcomes[
    `${dataRef.current.riskLikelihoodId}x${dataRef.current.riskSeverityId}`
  ]
    ? {
        rating:
          matrix.ratings[
            matrix.outcomes[
              `${dataRef.current.riskLikelihoodId}x${dataRef.current.riskSeverityId}`
            ]!
          ]!.name,
        ratingDescription:
          matrix.ratings[
            matrix.outcomes[
              `${dataRef.current.riskLikelihoodId}x${dataRef.current.riskSeverityId}`
            ]!
          ]!.description || 'No Description',
      }
    : undefined;

  const handleHardSave = React.useCallback(() => {
    if (checkIsDirty()) {
      dispatch(
        eventAseUpdateRiskRating({ key: eventKey, data: dataRef.current }),
      );
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [checkIsDirty, dispatch, eventKey]);

  const handleClose = React.useCallback(() => {
    if (checkIsDirty()) {
      confirmUnsaved({
        onSave: handleHardSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [checkIsDirty, confirmUnsaved, dispatch, handleHardSave]);

  const handleSoftSave = React.useCallback(
    (
      changes: Partial<Details>,
      { doForceUpdate }: { doForceUpdate?: 'always' | 'dirtyChange' },
    ) => {
      const newValues = { ...dataRef.current, ...changes };
      if (!isEqual(dataRef.current, newValues)) {
        dataRef.current = newValues;
        if (
          doForceUpdate === 'always' ||
          (doForceUpdate === 'dirtyChange' &&
            checkIsDirty() !== isDirtyRef.current)
        ) {
          forceUpdate();
        }
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const handleSoftSaveOption = (changes: Partial<Details>) => {
    handleSoftSave(changes, {});
    setCurrentlyEditing(undefined);
  };

  const isDirtyVar = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  const changeType =
    dataRef.current.riskLikelihoodId === ''
      ? 'nochange'
      : dataRef.current.riskLikelihoodId === '0'
        ? 'remove'
        : 'change';

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={`${complianceType.name} Risk Rating`}
          onPressCancelButton={handleClose}
          onPressSaveButton={handleHardSave}
        />
      }
    >
      {currentRisk ? (
        <Card label="Current Rating">
          <CardRow
            labelAlignTop
            label={`Likelihood: ${currentRisk.likelihood}`}
            value={currentRisk.likelihoodDescription}
          />
          <CardRow
            labelAlignTop
            label={`Severity: ${currentRisk.severity}`}
            value={currentRisk.severityDescription}
          />
          <CardRow
            labelAlignTop
            label={`Rating: ${currentRisk.rating}`}
            value={currentRisk.ratingDescription}
          />
        </Card>
      ) : (
        <Card label="Current Rating">
          <CardRow value="No Rating" />
        </Card>
      )}

      <Card label="Current Reason for Rating">
        <CardRow value={assetReason || 'No Reason'} />
      </Card>

      <SeparatorHorizontal />

      <Card>
        <CardRow
          style={{ flex: 1 }}
          label="Keep Current Rating"
          radioValue={changeType === 'nochange'}
          onPress={() =>
            handleSoftSave(
              {
                riskLikelihoodId: '',
                riskSeverityId: '',
              },
              { doForceUpdate: 'always' },
            )
          }
        />
        <CardRow
          style={{ flex: 1 }}
          label="Change Rating"
          radioValue={changeType === 'change'}
          onPress={() =>
            handleSoftSave(
              {
                riskLikelihoodId: !matrix.likelihoods[
                  dataRef.current.riskLikelihoodId
                ]
                  ? currentRisk?.likelihoodId || matrix.likelihood_ids[0]
                  : dataRef.current.riskLikelihoodId,
                riskSeverityId: !matrix.severities[
                  dataRef.current.riskSeverityId
                ]
                  ? currentRisk?.severityId || matrix.severity_ids[0]
                  : dataRef.current.riskSeverityId,
              },
              { doForceUpdate: 'always' },
            )
          }
        />
        <CardRow
          style={{ flex: 1 }}
          label="Remove Rating"
          radioValue={changeType === 'remove'}
          onPress={() =>
            handleSoftSave(
              {
                riskLikelihoodId: '0',
                riskSeverityId: '0',
              },
              { doForceUpdate: 'always' },
            )
          }
        />
      </Card>

      {changeType === 'change' && (
        <Card>
          {currentlyEditing === 'likelihood' ? (
            <Card
              marginHorizontal={false}
              expanded
              expandedInline
              expandedInlineFirst
            >
              {matrix.likelihood_ids.map((likelihoodId) => (
                <CardRow
                  key={likelihoodId}
                  radioValue={likelihoodId === dataRef.current.riskLikelihoodId}
                  label={matrix.likelihoods[likelihoodId]!.name}
                  labelAlignTop
                  value={
                    matrix.likelihoods[likelihoodId]?.description ||
                    'No Description'
                  }
                  onPress={() =>
                    handleSoftSaveOption({ riskLikelihoodId: likelihoodId })
                  }
                />
              ))}
            </Card>
          ) : (
            <CardRow
              label={`Likelihood: ${
                matrix.likelihoods[dataRef.current.riskLikelihoodId]?.name
              }`}
              labelAlignTop
              value={
                matrix.likelihoods[dataRef.current.riskLikelihoodId]
                  ?.description
              }
              onPress={() =>
                animateNext(() => setCurrentlyEditing('likelihood'))
              }
              navigationType="input-radio"
            />
          )}

          {currentlyEditing === 'severity' ? (
            <Card marginHorizontal={false} expanded expandedInline>
              {matrix.severity_ids.map((severityId) => (
                <CardRow
                  key={severityId}
                  radioValue={severityId === dataRef.current.riskSeverityId}
                  label={matrix.severities[severityId]!.name}
                  labelAlignTop
                  value={
                    matrix.severities[severityId]?.description ||
                    'No Description'
                  }
                  onPress={() =>
                    handleSoftSaveOption({ riskSeverityId: severityId })
                  }
                />
              ))}
            </Card>
          ) : (
            <CardRow
              label={`Severity: ${
                matrix.severities[dataRef.current.riskSeverityId]?.name
              }`}
              labelAlignTop
              value={
                matrix.severities[dataRef.current.riskSeverityId]?.description
              }
              onPress={() => animateNext(() => setCurrentlyEditing('severity'))}
              navigationType="input-radio"
            />
          )}

          {newRisk && (
            <CardRow
              label={`Rating: ${newRisk.rating}`}
              labelAlignTop
              value={`${newRisk.ratingDescription}`}
            />
          )}
        </Card>
      )}

      {changeType !== 'nochange' && (
        <Card>
          <CardRow
            label="Change Reason for Rating"
            checkedValue={!!dataRef.current.riskChangeReason}
            onPress={() =>
              dataRef.current.riskChangeReason
                ? handleSoftSave(
                    {
                      riskChangeReason: false,
                      riskReason: '',
                    },
                    { doForceUpdate: 'always' },
                  )
                : handleSoftSave(
                    {
                      riskChangeReason: true,
                      riskReason: assetReason,
                    },
                    { doForceUpdate: 'always' },
                  )
            }
          />
          {!!dataRef.current.riskChangeReason && (
            <TextAreaField
              placeholder="New reason for rating..."
              value={dataRef.current.riskReason}
              onChange={(value) =>
                handleSoftSave(
                  {
                    riskReason: value,
                  },
                  { doForceUpdate: 'dirtyChange' },
                )
              }
              onEsc={handleClose}
            />
          )}
        </Card>
      )}
    </Page>
  );
};
